import { forwardRef, type SelectHTMLAttributes } from 'react';

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** ラベル */
  label?: string;
  /** エラーメッセージ */
  error?: string;
  /** ヘルプテキスト */
  helperText?: string;
  /** 選択肢 */
  options: SelectOption[];
  /** プレースホルダー */
  placeholder?: string;
  /** 入力サイズ */
  size?: 'sm' | 'md' | 'lg';
  /** 全幅表示 */
  fullWidth?: boolean;
}

/** @graph-connects none */
const sizeStyles = {
  sm: 'px-3 py-1.5 text-sm pr-8',
  md: 'px-4 py-2 text-sm pr-10',
  lg: 'px-4 py-3 text-base pr-10',
};

/**
 * @graph-stack cortex-admin
 * @graph-domain Engineering
 * @graph-business セレクトボックス。ラベル・エラー・ヘルプテキスト・プレースホルダー・3段階サイズ・カスタム矢印を統合したスタイル付きセレクト
 * @graph-connects none
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      error,
      helperText,
      options,
      placeholder,
      size = 'md',
      fullWidth = false,
      className = '',
      id,
      disabled,
      ...props
    },
    ref,
  ) => {
    const selectId = id ?? `select-${Math.random().toString(36).substring(2, 11)}`;
    const hasError = Boolean(error);

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label
            htmlFor={selectId}
            className="block text-sm font-medium text-[var(--color-text-secondary)] mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            disabled={disabled}
            className={`
              w-full appearance-none
              bg-[var(--color-bg-surface)]
              border rounded-lg
              text-[var(--color-text-primary)]
              transition-all duration-[var(--transition-fast)]
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-[var(--color-bg-base)]
              disabled:opacity-50 disabled:cursor-not-allowed
              cursor-pointer
              ${
                hasError
                  ? 'border-[var(--color-error)] focus:ring-[var(--color-error)]'
                  : 'border-[var(--color-border)] hover:border-[var(--color-border-hover)] focus:border-[var(--color-border-focus)] focus:ring-[var(--color-border-focus)]'
              }
              ${sizeStyles[size]}
              ${className}
            `}
            aria-invalid={hasError}
            aria-describedby={
              hasError ? `${selectId}-error` : helperText ? `${selectId}-helper` : undefined
            }
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          {/* ドロップダウン矢印 */}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[var(--color-text-muted)]">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
        {error && (
          <p id={`${selectId}-error`} className="mt-1.5 text-sm text-[var(--color-error)]">
            {error}
          </p>
        )}
        {helperText && !error && (
          <p id={`${selectId}-helper`} className="mt-1.5 text-sm text-[var(--color-text-muted)]">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';
